import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
	Client,
	CognitoUser,
	JourneyDistrictReportData,
	JourneySchoolReportData,
	ReportingSectionReport,
	ReportingSectionReportFilter,
	ReportingStudentLessonProgressFilter,
	ReportingStudentLessonProgressReport,
	ReportingStudentLessonProgressReportEntry,
	ReportingStudentLessonProgressResult,
	ReportingStudentReport,
	ReportingStudentReportFilter,
	ReportingStudentTakeReport,
	TakeStatus,
	_,
} from '@mira-avant/mtlibs';
import { AuthService } from './auth.service';
import { ClientService } from './client.service';
import { ApiService } from './api.service';
import { JourneySchoolReportFilterForm } from '../components/forms/standard/journey-school-report-filter-form/journey-school-report-filter-form';

@Injectable({
	providedIn: 'root',
})
export class JourneyReports {
	private readonly BASE_PATH = 'journey-reports';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	constructor(
		private api: ApiService,
		private authService: AuthService,
		private clientService: ClientService
	) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async getJourneyDistrictReport$(productName: string): Promise<JourneyDistrictReportData> {
		return this.withLoading(async () => {
			return firstValueFrom(
				this.api.post<JourneyDistrictReportData>(`${this.BASE_PATH}/district-report/${productName}`, {})
			);
		});
	}

	public async getJourneySchoolReport$(
		productName: string,
		filter: JourneySchoolReportFilterForm
	): Promise<JourneySchoolReportData> {
		return this.withLoading(async () => {
			return firstValueFrom(
				this.api.post<JourneySchoolReportData>(`${this.BASE_PATH}/school-report/${productName}`, filter)
			);
		});
	}

	public async getContentTakes$(
		activityUuid: string,
		sectionUuid: string,
		studentUuid: string
	): Promise<ReportingStudentTakeReport> {
		return this.withLoading(async () => {
			return firstValueFrom(
				this.api.post<ReportingStudentTakeReport>(`${this.BASE_PATH}/content-takes`, {
					activityUuid,
					sectionUuid,
					studentUuid,
				})
			);
		});
	}

	public async getStudentLessonProgress$(
		courseUuid: string,
		sectionUuid: string,
		locationUuid: string,
		unitUuid: string,
		lessonUuid: string
	): Promise<ReportingStudentLessonProgressReport> {
		return this.withLoading(async () => {
			const student: CognitoUser = await firstValueFrom(this.authService.user$);
			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const filter: ReportingStudentLessonProgressFilter = {
				clientUuid: client.uuid,
				studentCognitoUuid: student.uuid,
				courseUuid,
				sectionUuid,
				locationUuid,
				unitUuid,
				lessonUuid,
			};

			return firstValueFrom(
				this.api.post<ReportingStudentLessonProgressResult>(`${this.BASE_PATH}/student-lesson-progress`, filter)
			).then((result: ReportingStudentLessonProgressResult) => {
				// Group reportings by activity
				const reportingsByActivity = result.reporting.reduce((acc: { [key: string]: any[] }, item: any) => {
					const key = item.activity_uuid;
					if (!acc[key]) {
						acc[key] = [];
					}
					acc[key].push(item);
					return acc;
				}, {});

				// Create the report entries array
				const report: ReportingStudentLessonProgressReportEntry[] = Object.entries(reportingsByActivity).map(
					([activityUuid, activityReportings]) => {
						// An activity is completed if all content items are completed
						const isCompleted = activityReportings.every(r => r.status === TakeStatus.COMPLETED);

						// An activity is started if at least one content item is started
						const isStarted = activityReportings.some(
							r => r.status === TakeStatus.IN_PROGRESS || r.status === TakeStatus.COMPLETED
						);

						// Determine the overall status
						let status: TakeStatus;

						if (isCompleted) {
							status = TakeStatus.COMPLETED;
						} else if (isStarted) {
							status = TakeStatus.IN_PROGRESS;
						} else {
							status = TakeStatus.NOT_STARTED;
						}

						return {
							activityUuid,
							status,
						};
					}
				);

				return {
					report,
				};
			});
		});
	}

	public async getStudentReport$(
		studentUuid: string,
		sectionUuid: string,
		startDate: Date,
		endDate: Date
	): Promise<ReportingStudentReport> {
		return this.withLoading(async () => {
			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const filter: ReportingStudentReportFilter = {
				clientUuid: client.uuid,
				studentUuid,
				sectionUuid,
				startDate,
				endDate,
			};

			return firstValueFrom(this.api.post<ReportingStudentReport>(`${this.BASE_PATH}/student-report`, filter));
		});
	}

	public async getSectionReport$(
		sectionUuid: string,
		startDate: Date,
		endDate: Date
	): Promise<ReportingSectionReport> {
		return this.withLoading(async () => {
			const client: Client = await firstValueFrom(this.clientService.selectedClient$);

			const filter: ReportingSectionReportFilter = {
				clientUuid: client.uuid,
				sectionUuid,
				startDate,
				endDate,
			};

			return firstValueFrom(this.api.post<ReportingSectionReport>(`${this.BASE_PATH}/section-report`, filter));
		});
	}
}
